export default class {
    constructor(options = {}) {
        this.hiddenAt = null;
        this.hasLeftTab = false;
        this.offlineAt = null;
        this.hasLostNetwork = false;
        this.isRecovering = false;
        this.offlineCallbacks = [];
        this.onlineCallbacks = [];

        // Головний параметр — мінімальний час відсутності для refresh
        this.RECOVERY_THRESHOLD = options.threshold ?? 4000; // мс

        // Час бездіяльності після якого — reset + redirect (за замовчуванням 30 хв)
        this.INACTIVITY_TIMEOUT = options.inactivityTimeout ?? 30 * 60 * 1000; // мс

        this.debug = options.debug ?? true;
        this.onRecover = typeof options.onRecover === "function" ? options.onRecover : null;
        this.onInactivity = typeof options.onInactivity === "function" ? options.onInactivity : null;
        this.onDateChange = typeof options.onDateChange === "function" ? options.onDateChange : null;

        // Дата старту сесії (для відстеження зміни дня)
        this._sessionDate = this._todayString();

        // Timer ID для inactivity
        this._inactivityTimer = null;

        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleOffline = this.handleOffline.bind(this);
        this.handleOnline = this.handleOnline.bind(this);
        this.handleActivity = this.handleActivity.bind(this);
    }

    // Повертає поточну дату у форматі "YYYY-MM-DD"
    _todayString() {
        const d = new Date();
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    }

    // Чи змінився день відносно старту сесії
    _hasDateChanged() {
        return this._todayString() !== this._sessionDate;
    }

    _startInactivityTimer() {
        this._clearInactivityTimer();
        this._inactivityTimer = setTimeout(() => {
            if (this.debug) {
                console.log(`[TabRecoveryService] Inactivity timeout (${this.INACTIVITY_TIMEOUT} ms) triggered`);
            }
            if (this.onInactivity) {
                this.onInactivity();
            }
        }, this.INACTIVITY_TIMEOUT);
    }

    _clearInactivityTimer() {
        if (this._inactivityTimer !== null) {
            clearTimeout(this._inactivityTimer);
            this._inactivityTimer = null;
        }
    }

    handleActivity() {
        // Будь-яка активність юзера — перезапускаємо таймер
        this._startInactivityTimer();
    }

    init() {
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
        window.addEventListener("focus", this.handleFocus);
        window.addEventListener("offline", this.handleOffline);
        window.addEventListener("online", this.handleOnline);

        // Слухаємо будь-яку активність для inactivity timer
        ["mousemove", "keydown", "touchstart", "scroll", "click"].forEach((event) => {
            window.addEventListener(event, this.handleActivity, { passive: true });
        });

        // Запускаємо таймер бездіяльності одразу
        this._startInactivityTimer();

        if (this.debug) {
            console.log(
                `[TabRecoveryService] initialized (threshold: ${this.RECOVERY_THRESHOLD} ms, inactivity: ${this.INACTIVITY_TIMEOUT} ms)`
            );
        }
    }

    destroy() {
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        window.removeEventListener("focus", this.handleFocus);
        window.removeEventListener("offline", this.handleOffline);
        window.removeEventListener("online", this.handleOnline);

        ["mousemove", "keydown", "touchstart", "scroll", "click"].forEach((event) => {
            window.removeEventListener(event, this.handleActivity);
        });

        this._clearInactivityTimer();
        this.resetState();

        if (this.debug) {
            console.log("[TabRecoveryService] destroyed");
        }
    }

    handleVisibilityChange() {
        if (document.visibilityState === "hidden") {
            this.onTabHidden();
            return;
        }

        if (document.visibilityState === "visible") {
            this.tryRecover("visibilitychange");
        }
    }

    handleFocus() {
        this.tryRecover("focus");
    }

    onTabHidden() {
        // якщо вже зафіксовано вихід — не перезаписуємо
        if (this.hiddenAt) return;

        this.hiddenAt = Date.now();
        this.hasLeftTab = true;

        if (this.debug) {
            console.log("[TabRecoveryService] Tab hidden at:", this.hiddenAt);
        }
    }

    handleOffline() {
        this.offlineCallbacks.forEach((cb) => cb());

        if (this.offlineAt) return;

        this.offlineAt = Date.now();
        this.hasLostNetwork = true;

        if (this.debug) {
            console.log("[TabRecoveryService] Offline at:", this.offlineAt);
        }
    }

    handleOnline() {
        this.onlineCallbacks.forEach((cb) => cb());
        this.tryRecoverNetwork("online");
    }

    onDisconnect(callback) {
        if (typeof callback === "function") {
            this.offlineCallbacks.push(callback);
        }
    }

    onReconnect(callback) {
        if (typeof callback === "function") {
            this.onlineCallbacks.push(callback);
        }
    }

    async tryRecover(source) {
        // 🔴 КРИТИЧНО: якщо hiddenAt вже очищений — значить recovery вже почався
        if (!this.hiddenAt) return;

        // Guard від подвійних тригерів (focus + visibilitychange)
        if (!this.hasLeftTab || this.isRecovering) return;

        // Зберігаємо timestamp і ОДРАЗУ очищаємо,
        // щоб другий евент нічого не зробив
        const hiddenTimestamp = this.hiddenAt;
        this.hiddenAt = null;

        const now = Date.now();
        const awayTime = now - hiddenTimestamp;

        if (this.debug) {
            console.log(`[TabRecoveryService] User returned via ${source}. Away time: ${awayTime} ms`);
        }

        // � Перевірка зміни дати — найвищий пріоритет
        if (this._hasDateChanged()) {
            if (this.debug) {
                console.log(
                    `[TabRecoveryService] Date changed (${
                        this._sessionDate
                    } → ${this._todayString()}). Triggering onDateChange.`
                );
            }
            this._sessionDate = this._todayString();
            this.resetFlagsOnly();
            try {
                if (this.onDateChange) {
                    await this.onDateChange({ source, awayTime });
                }
            } catch (err) {
                console.error("[TabRecoveryService] onDateChange callback failed:", err);
            }
            return;
        }

        // �🔵 Логіка порогу (threshold)
        if (awayTime < this.RECOVERY_THRESHOLD) {
            if (this.debug) {
                console.log(`[TabRecoveryService] Ignored recovery (away < ${this.RECOVERY_THRESHOLD} ms)`);
            }
            this.resetFlagsOnly();
            return;
        }

        // Початок recovery (захист від гонок)
        this.isRecovering = true;

        console.log(`[TabRecoveryService] Recovery triggered (away ${awayTime} ms >= ${this.RECOVERY_THRESHOLD} ms)`);

        try {
            if (this.onRecover) {
                await this.onRecover({ source, awayTime });
            }
        } catch (err) {
            console.error("[TabRecoveryService] Recovery callback failed:", err);
        } finally {
            this.resetState();
        }
    }

    async tryRecoverNetwork(source) {
        if (!this.offlineAt) return;
        if (!this.hasLostNetwork || this.isRecovering) return;

        const lostTimestamp = this.offlineAt;
        this.offlineAt = null;

        const now = Date.now();
        const awayTime = now - lostTimestamp;

        if (this.debug) {
            console.log(`[TabRecoveryService] Network restored via ${source}. Offline time: ${awayTime} ms`);
        }

        if (awayTime < this.RECOVERY_THRESHOLD) {
            if (this.debug) {
                console.log(`[TabRecoveryService] Ignored network recovery (offline < ${this.RECOVERY_THRESHOLD} ms)`);
            }
            this.resetFlagsOnly();
            return;
        }

        this.isRecovering = true;
        console.log(
            `[TabRecoveryService] Network recovery triggered (offline ${awayTime} ms >= ${this.RECOVERY_THRESHOLD} ms)`
        );

        try {
            if (this.onRecover) {
                await this.onRecover({ source, awayTime });
            }
        } catch (err) {
            console.error("[TabRecoveryService] Network recovery callback failed:", err);
        } finally {
            this.resetState();
        }
    }

    resetFlagsOnly() {
        this.hasLeftTab = false;
        this.hasLostNetwork = false;
        this.isRecovering = false;
    }

    resetState() {
        this.hiddenAt = null;
        this.hasLeftTab = false;
        this.offlineAt = null;
        this.hasLostNetwork = false;
        this.isRecovering = false;
    }
}
